import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useOutletContext, useSearchParams } from 'react-router'

import { ErrorDeLaApi, consulta, pedir } from '../../api/nucleo'
import type { Sitio } from '../../api/tipos'
import { Aviso, Boton, BotonDeIcono, EstadoVacio, Esqueleto, Pastilla, Pestanas, Titulo, clases } from '../../componentes/ui'
import type { Contexto } from '../Modulo'
import { carriles, ESTADOS, esFecha, horaDeMin, hoyEn, lunesDe, rangoDelDia, sumarDias, textoDeFecha, textoDeSemana } from './calendario'
import { FichaDeReserva } from './FichaDeReserva'
import { NuevaReserva } from './NuevaReserva'
import type { Calendario, ReservaDelCalendario } from './tipos'

/**
 * La agenda del panel (CLAUDE.md §33, docs/agenda.md §5): el día con una columna por recurso, la
 * semana, y la lista —lo que se mira en el móvil por la mañana—. Todo lo pinta con lo que da
 * `GET /api/agenda/calendario`, con las horas ya en la zona del negocio. Quien solo ve su agenda
 * (preset «Profesional») recibe solo lo suyo.
 */

type Vista = 'dia' | 'lista' | 'semana'
const PX_POR_HORA = 56

const COLOR: Record<string, string> = {
  'por-pagar': 'border-linea-fuerte bg-papel-2 border-dashed',
  pendiente: 'border-aviso bg-aviso-suave',
  confirmada: 'border-violeta bg-violeta-suave',
  asistio: 'border-ok bg-ok-suave',
  'no-vino': 'border-error bg-error-suave',
  cancelada: 'border-linea bg-papel-2 text-tenue line-through',
}

export const Reservas = () => {
  const { espacio, permisos, sitio } = useOutletContext<Contexto>()
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (permisos.cargando) return <Esqueleto />
  if (!permisos.tiene('agenda.ver')) return <Aviso tono="info">La agenda la ve quien atiende las reservas. Si te hace falta, pídeselo al propietario de la cuenta.</Aviso>
  return <Pantalla espacio={espacio} key={sitio.id} puedeGestionar={permisos.tiene('agenda.gestionar')} sitio={sitio} />
}

const Pantalla = ({ espacio, sitio, puedeGestionar }: { espacio: number; sitio: Sitio; puedeGestionar: boolean }) => {
  const cliente = useQueryClient()
  const [parametros, setParametros] = useSearchParams()
  const movil = typeof window !== 'undefined' && window.innerWidth < 640
  const vista: Vista = (['dia', 'semana', 'lista'] as const).find((v) => v === parametros.get('vista')) ?? (movil ? 'lista' : 'dia')
  const fecha = esFecha(parametros.get('fecha')) ? parametros.get('fecha')! : hoyEn()
  const desde = vista === 'semana' ? lunesDe(fecha) : fecha
  const dias = vista === 'semana' ? 7 : 1
  const [abierta, setAbierta] = useState<null | number>(null)
  const [nueva, setNueva] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['agenda-calendario', sitio.id, desde, dias],
    queryFn: () => pedir<Calendario>(`/agenda/calendario${consulta({ sitio: sitio.id, desde, dias })}`),
  })
  const ir = (cambios: Partial<{ fecha: string; vista: Vista }>) => {
    const p = new URLSearchParams(parametros)
    if (cambios.fecha) p.set('fecha', cambios.fecha)
    if (cambios.vista) p.set('vista', cambios.vista)
    setParametros(p, { replace: true })
  }
  const paso = vista === 'semana' ? 7 : 1
  const refrescar = () => cliente.invalidateQueries({ queryKey: ['agenda-calendario', sitio.id] })
  const reserva = data?.reservas.find((r) => r.id === abierta) ?? null

  return (
    <>
      <Titulo
        accion={
          puedeGestionar && data ? (
            <Boton icono={<Plus />} onClick={() => setNueva(true)} tipo="primario">
              Nueva reserva
            </Boton>
          ) : null
        }
        descripcion={data?.soloLaSuya ? 'Tu agenda: las reservas de lo que atiendes tú.' : 'Las reservas de tu web, de tu teléfono y de tu WhatsApp, en un sitio.'}
        rotulo={sitio.nombre}
      >
        Agenda
      </Titulo>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <BotonDeIcono etiqueta={vista === 'semana' ? 'Semana anterior' : 'Día anterior'} onClick={() => ir({ fecha: sumarDias(fecha, -paso) })}>
            <ChevronLeft />
          </BotonDeIcono>
          <Boton onClick={() => ir({ fecha: hoyEn(data?.zona) })} pequeno>
            Hoy
          </Boton>
          <BotonDeIcono etiqueta={vista === 'semana' ? 'Semana siguiente' : 'Día siguiente'} onClick={() => ir({ fecha: sumarDias(fecha, paso) })}>
            <ChevronRight />
          </BotonDeIcono>
        </div>
        <h2 className="font-display text-lg font-semibold first-letter:uppercase">{vista === 'semana' ? textoDeSemana(desde) : textoDeFecha(fecha)}</h2>
        <input aria-label="Ir a una fecha" className="h-8 rounded-boton border border-linea bg-papel px-2 text-[13px]" onChange={(e) => esFecha(e.target.value) && ir({ fecha: e.target.value })} type="date" value={fecha} />
      </div>
      <Pestanas
        activa={vista}
        alCambiar={(v) => ir({ vista: v as Vista })}
        pestanas={[
          { clave: 'dia', etiqueta: 'Día' },
          { clave: 'semana', etiqueta: 'Semana' },
          { clave: 'lista', etiqueta: 'Lista' },
        ]}
      />
      <div className="mt-4">
        {error ? (
          <Aviso tono={error instanceof ErrorDeLaApi && error.status === 404 ? 'info' : 'error'}>
            {error instanceof ErrorDeLaApi && error.status === 404 ? (
              <>
                Esta web todavía no tiene la agenda activada. <Link className="font-medium text-violeta underline" to={`/e/${espacio}/s/${sitio.id}/agenda`}>Actívala aquí</Link>.
              </>
            ) : error instanceof ErrorDeLaApi ? (
              error.message
            ) : (
              'No se ha podido cargar la agenda. Recarga la página.'
            )}
          </Aviso>
        ) : isPending || !data ? (
          <Esqueleto />
        ) : data.recursos.length === 0 ? (
          <EstadoVacio
            descripcion={data.soloLaSuya ? 'Todavía no tienes ningún recurso asignado. Pídele al propietario que te asigne en Agenda → Recursos.' : 'Añade quién o qué se reserva en Agenda → Recursos.'}
            icono={<CalendarDays />}
            titulo="Nada que reservar todavía"
          />
        ) : vista === 'lista' ? (
          <Lista abrir={setAbierta} datos={data} />
        ) : (
          <Rejilla abrir={setAbierta} datos={data} semana={vista === 'semana'} />
        )}
      </div>
      {reserva && data && (
        <FichaDeReserva
          cambiada={() => {
            void refrescar()
          }}
          cerrar={() => setAbierta(null)}
          datos={data}
          espacio={espacio}
          puedeGestionar={puedeGestionar}
          reserva={reserva}
          sitio={sitio.id}
        />
      )}
      {nueva && data && (
        <NuevaReserva
          cerrar={() => setNueva(false)}
          datos={data}
          fecha={fecha}
          hecha={() => {
            setNueva(false)
            void refrescar()
          }}
          sitio={sitio.id}
        />
      )}
    </>
  )
}

const nombreDeRecurso = (datos: Calendario, id: number) => datos.recursos.find((r) => r.id === id)?.nombre ?? ''

const Lista = ({ datos, abrir }: { datos: Calendario; abrir: (id: number) => void }) => {
  const porDia = datos.dias.map((d) => ({ ...d, reservas: datos.reservas.filter((r) => r.fecha === d.fecha) }))
  if (datos.reservas.length === 0) return <p className="text-sm text-tenue">No hay reservas. Cuando alguien reserve, o apuntes una, aparecerá aquí.</p>
  return (
    <div className="flex flex-col gap-5">
      {porDia
        .filter((d) => d.reservas.length > 0)
        .map((d) => (
          <section key={d.fecha}>
            <h3 className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">{textoDeFecha(d.fecha)}</h3>
            <ul className="divide-y divide-linea rounded-caja border border-linea bg-papel">
              {d.reservas.map((r) => (
                <li key={r.id}>
                  <button className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-papel-2" onClick={() => abrir(r.id)} type="button">
                    <span className="w-24 shrink-0 font-medium tabular-nums">
                      {r.hora}–{r.horaFin}
                    </span>
                    <span className={clases('min-w-0 flex-1', r.estado === 'cancelada' && 'text-tenue line-through')}>
                      <span className="font-medium">{r.nombre}</span>
                      <span className="block text-xs text-tenue">
                        {r.servicio?.nombre ?? ''}
                        {r.recursos.length ? ` · ${r.recursos.map((x) => nombreDeRecurso(datos, x)).join(' y ')}` : ''}
                        {r.cantidad > 1 ? ` · ${r.cantidad}` : ''}
                      </span>
                    </span>
                    <Pastilla clave={ESTADOS[r.estado]?.clave ?? 'neutra'}>{ESTADOS[r.estado]?.texto ?? r.estado}</Pastilla>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  )
}

const Bloque = ({ r, arriba, alto, carril, de, titulo, abrir }: { r: ReservaDelCalendario; arriba: number; alto: number; carril: number; de: number; titulo: string; abrir: (id: number) => void }) => (
  <button
    className={clases('absolute overflow-hidden rounded-[6px] border-l-[3px] px-1.5 py-0.5 text-left text-[12px] leading-tight shadow-suave hover:z-10 hover:shadow-alta', COLOR[r.estado] ?? COLOR.confirmada)}
    onClick={() => abrir(r.id)}
    style={{ top: arriba, height: Math.max(22, alto - 2), left: `calc(${(carril / de) * 100}% + 2px)`, width: `calc(${100 / de}% - 4px)` }}
    title={titulo}
    type="button"
  >
    <span className="font-semibold tabular-nums">{r.hora}</span> <span className="font-medium">{r.nombre}</span>
    {alto > 36 && <span className="block truncate text-tinta-2">{titulo}</span>}
  </button>
)

const Rejilla = ({ datos, semana, abrir }: { datos: Calendario; semana: boolean; abrir: (id: number) => void }) => {
  const visibles = useMemo(() => datos.reservas.filter((r) => r.estado !== 'cancelada'), [datos.reservas])
  // En la semana, una columna por día con todos los recursos; en el día, una por recurso.
  const columnas = semana
    ? datos.dias.map((d) => ({ clave: d.fecha, titulo: textoDeFecha(d.fecha, true), abierto: datos.abierto.filter((a) => a.fecha === d.fecha), reservas: visibles.filter((r) => r.fecha === d.fecha) }))
    : datos.recursos.map((rec) => ({ clave: String(rec.id), titulo: rec.nombre, abierto: datos.abierto.filter((a) => a.recurso === rec.id), reservas: visibles.filter((r) => r.recursos.includes(rec.id)) }))
  const [inicio, fin] = rangoDelDia([...datos.abierto, ...visibles.map((r) => ({ desdeMin: r.inicioMin, hastaMin: Math.min(r.finMin, 24 * 60) }))])
  const alto = ((fin - inicio) / 60) * PX_POR_HORA
  const y = (min: number) => ((Math.max(inicio, Math.min(fin, min)) - inicio) / 60) * PX_POR_HORA
  const horas = Array.from({ length: (fin - inicio) / 60 + 1 }, (_, i) => inicio + i * 60)
  return (
    <div className="overflow-x-auto rounded-caja border border-linea bg-papel">
      <div className="grid min-w-max" style={{ gridTemplateColumns: `56px repeat(${columnas.length}, minmax(${semana ? 120 : 160}px, 1fr))` }}>
        <div className="sticky top-0 border-b border-linea bg-papel" />
        {columnas.map((c) => (
          <div className="border-b border-l border-linea px-2 py-2 text-[13px] font-medium first-letter:uppercase" key={c.clave}>
            {c.titulo}
          </div>
        ))}
        <div className="relative" style={{ height: alto }}>
          {horas.map((h) => (
            <span className="absolute right-2 -translate-y-1/2 text-[11px] text-tenue tabular-nums" key={h} style={{ top: y(h) }}>
              {horaDeMin(h)}
            </span>
          ))}
        </div>
        {columnas.map((c) => {
          const reparto = carriles(c.reservas)
          return (
            <div className="relative border-l border-linea bg-papel-2" key={c.clave} style={{ height: alto }}>
              {c.abierto.map((a, i) => (
                <div className="absolute inset-x-0 bg-papel" key={i} style={{ top: y(a.desdeMin), height: y(a.hastaMin) - y(a.desdeMin) }} />
              ))}
              {horas.map((h) => (
                <div className="absolute inset-x-0 border-t border-linea/60" key={h} style={{ top: y(h) }} />
              ))}
              {c.reservas.map((r) => {
                const lugar = reparto.get(r.id) ?? { carril: 0, de: 1 }
                const titulo = [r.servicio?.nombre, semana ? r.recursos.map((x) => nombreDeRecurso(datos, x)).join(' y ') : null, r.cantidad > 1 ? `${r.cantidad}` : null].filter(Boolean).join(' · ')
                return <Bloque abrir={abrir} alto={y(r.finMin) - y(r.inicioMin)} arriba={y(r.inicioMin)} carril={lugar.carril} de={lugar.de} key={r.id} r={r} titulo={titulo} />
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
